import React, { useState } from 'react';
import { AVATARS } from '../constants';
import Modal from './Modal';

const FEATURES = [
  ['🗺️', '8x8 Grid'],
  ['🧠', 'BFS AI Placement'],
  ['⚡', 'Energy System'],
  ['📈', 'Level Scaling'],
  ['🏆', 'Leaderboard'],
  ['📱', 'Mobile Ready'],
];

const FLOATERS = ['💰', '🍔', '⚡', '🎮', '🍎', '🏆'];

function ProfileModal({ profile, onClose }) {
  const av = AVATARS.find(a => a.id === profile.avatarId) || AVATARS[0];
  return (
    <Modal title="👤 Your Profile" onClose={onClose}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: '80px', height: '80px', borderRadius: '50%',
          background: av.bg, fontSize: '2.5rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 12px',
          border: '3px solid #FFD93D',
          boxShadow: '0 0 20px rgba(255,217,61,0.4)',
        }}>
          {av.emoji}
        </div>
        <div style={{ fontFamily: 'Fredoka One', fontSize: '1.5rem', color: '#FFD93D' }}>
          {profile.username}
        </div>
        <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '20px' }}>{av.label}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          <div className="stat-card">
            <div className="stat-value">{profile.totalGames || 0}</div>
            <div className="stat-label">Games</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{profile.bestScore || 0}</div>
            <div className="stat-label">Best Score</div>
          </div>
        </div>
        {profile.lastPlayed && (
          <div style={{ color: '#666', fontSize: '0.8rem' }}>
            Last played: {profile.lastPlayed}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default function HomePage({ profile, onPlay, onLogin, onRegister, leaderboard, loadingLB }) {
  const [showProfile, setShowProfile] = useState(false);

  return (
    <div className="home-page">
      <div className="hero-bg" />

      {FLOATERS.map((e, i) => (
        <div key={i} className="floating-emoji" style={{
          top:  `${10 + i * 13}%`,
          left: `${5  + i * 15}%`,
          animationDelay:    `${i * 0.5}s`,
          animationDuration: `${2.5 + i * 0.4}s`,
        }}>{e}</div>
      ))}

      {/* HERO */}
      <div className="hero-section">
        <div className="hero-badge">⚡ BFS-Powered Grid Game</div>
        <h1 className="hero-title">Resource Rush!</h1>

        <div className="hero-desc-box">
          <div className="hero-desc-title"><span>⚡</span> Welcome to Resource Rush!</div>
          Move around the grid, collect food to manage your energy, and gather money to
          increase your score. Each move costs energy, the game speeds up over time, and
          the grid wraps around the edges.
          <br /><br />
          Balance your energy carefully — if it reaches zero <strong>or</strong> exceeds
          the limit, the game ends.
          <br /><br />
          <strong style={{ color: '#FFD93D' }}>
            Your goal: collect as much money as possible and beat the leaderboard. 🎮
          </strong>
          <br /><br />
          <em style={{ color: '#888' }}>Want to try it out?</em>
        </div>

        <div className="hero-btns">
          <button className="btn-primary" onClick={() => profile ? onPlay() : onLogin()}>
            🚀 LET'S GO!
          </button>
          {!profile ? (
            <button className="btn-secondary" onClick={onRegister}>
              🆕 New? Register
            </button>
          ) : (
            <button className="btn-secondary" onClick={() => setShowProfile(true)}>
              👤 My Profile
            </button>
          )}
        </div>
      </div>

      {/* FEATURE CHIPS */}
      <div className="features-strip">
        {FEATURES.map(([icon, label]) => (
          <div key={label} className="feature-chip">
            <span>{icon}</span><span>{label}</span>
          </div>
        ))}
      </div>

      {/* LEADERBOARD */}
      <div className="section">
        <div className="section-title">🏆 Leaderboard</div>
        <div className="leaderboard-table">
          <div className="lb-row header">
            <div>#</div>
            <div>Player</div>
            <div className="lb-score">Best</div>
            <div className="lb-time">Games</div>
          </div>

          {loadingLB ? (
            <div style={{ padding: '24px', textAlign: 'center', color: '#555', fontSize: '0.9rem' }}>
              ⏳ Loading...
            </div>
          ) : leaderboard.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#555', fontSize: '0.9rem' }}>
              No scores yet — be the first! 🎮
            </div>
          ) : (
            leaderboard.map((entry, i) => {
              const av = AVATARS.find(a => a.id === (entry.avatar_id || entry.avatarId)) || AVATARS[0];
              return (
                <div className="lb-row" key={entry.username}>
                  <div style={{ fontFamily: 'Fredoka One', fontSize: '1.1rem' }}>
                    {['🥇', '🥈', '🥉'][i] || `${i + 1}`}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700 }}>
                    <span>{av.emoji}</span> {entry.username}
                  </div>
                  <div className="lb-score">{entry.best_score ?? entry.bestScore}</div>
                  <div className="lb-time">{entry.total_games ?? entry.totalGames}x</div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {showProfile && profile && (
        <ProfileModal profile={profile} onClose={() => setShowProfile(false)} />
      )}
    </div>
  );
}